import {
  BaseEditor,
  Editor,
  Element as SlateElement,
  Node,
  Path,
  Transforms,
} from 'slate'

export function withLists<T extends BaseEditor>(editor: T): T {
  const { insertBreak } = editor

  editor.insertBreak = () => {
    const { selection } = editor
    if (selection) {
      const [match] = Array.from(
        Editor.nodes(editor, {
          match: (n: Node & { type: string }) =>
            SlateElement.isElement(n) && n.type === 'list-item',
        })
      )

      if (match) {
        const [, path] = match
        const start = Editor.start(editor, path)
        const end = Editor.end(editor, path)
        const isEmptyListItem =
          Editor.string(editor, { anchor: start, focus: end }) === ''

        if (isEmptyListItem) {
          const [, itemPath] = match

          const [listMatch] = Array.from(
            Editor.nodes(editor, {
              match: (n: Node & { type: string }) =>
                SlateElement.isElement(n) &&
                ['bulleted-list', 'numbered-list', 'alpha-list'].includes(
                  n.type
                ),
            })
          )

          if (listMatch) {
            const [listNode, listPath] = listMatch

            if ((listNode as any).children.length <= 1) {
              Transforms.unwrapNodes(editor, { at: listPath })
              Transforms.setNodes(editor, { type: 'p' } as any)
            } else {
              Transforms.removeNodes(editor, { at: itemPath })
              const afterListPath = Path.next(listPath)
              Transforms.insertNodes(
                editor,
                { type: 'p', children: [{ text: '' }] } as any,
                { at: afterListPath }
              )
              Transforms.select(editor, afterListPath)
            }
          }
          return
        }
      }
    }

    insertBreak()
  }

  return editor
}

/**
 * Soft-break (Shift+Enter) dentro de un `list-item`: inserta un salto de
 * línea `\n` en la MISMA leaf en lugar de crear un nuevo `list-item`. Así se
 * deja una línea en blanco dentro de la lista sin generar otro número.
 *
 * Devuelve `true` si manejó el soft-break (el caller debe `preventDefault`),
 * `false` si el cursor no está dentro de un `list-item` (Enter normal sigue su
 * curso).
 */
export function handleListSoftBreak<T extends BaseEditor>(editor: T): boolean {
  const { selection } = editor
  if (!selection) return false

  const [match] = Array.from(
    Editor.nodes(editor, {
      match: (n: Node & { type: string }) =>
        SlateElement.isElement(n) && n.type === 'list-item',
    })
  )

  if (!match) return false

  editor.insertText('\n')
  return true
}
